import NeptuneElement from "../../NeptuneElement.js";

class Tab extends NeptuneElement {
    
    templateMarkup = `
        <button role="tab" aria-selected="false" tabindex="-1">
            <i class="fa-regular"></i>
            <slot></slot>
        </button>
    `;

    componentCssUrl = '/src/components/container/tabgroup/tab.css';

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(Tab.observedAttributes);
        this.handleClick = this.handleClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.setEventListeners();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['active', 'icon'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        const tabButton = this.shadowRoot.querySelector('button');
        if (!tabButton) {
            return;
        }

        switch (name) {
            case 'icon': {
                this.setIcon();
                break;
            }
            case 'active': {
                const isActive = this.hasAttribute('active');
                tabButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
                tabButton.setAttribute('tabindex', isActive ? '0' : '-1');
                break;
            }
            default: {
                break;
            }
        }
    }

    setIcon() {
        const leftIcon = this.shadowRoot.querySelector('i');
        if (leftIcon) {
            const iconValue = this.getAttribute('icon');
            if (iconValue) {
                leftIcon.className = `fa-regular ${iconValue}`;
                leftIcon.removeAttribute('hidden');
            } else {
                leftIcon.className = 'fa-regular';
                leftIcon.setAttribute('hidden', '');
            }
        }
    }

    handleClick() {
        const tabGroup = this.closest('neptune-tab-group');
        if (tabGroup) {
            const tabId = this.getAttribute('id');
            tabGroup.activateTab(tabId);
        }
    }

    handleKeyDown(e) {
        const tabGroup = this.closest('neptune-tab-group');
        if (!tabGroup) return;

        const isVertical = tabGroup.getAttribute('orientation') === 'vertical';

        switch (e.key) {
            case 'ArrowRight':
            case 'Right':
                if (!isVertical) {
                    e.preventDefault();
                    tabGroup.focusNextTab(this, false);
                }
                break;
            case 'ArrowLeft':
            case 'Left':
                if (!isVertical) {
                    e.preventDefault();
                    tabGroup.focusPreviousTab(this, false);
                }
                break;
            case 'ArrowDown':
            case 'Down':
                if (isVertical) {
                    e.preventDefault();
                    tabGroup.focusNextTab(this, false);
                }
                break;
            case 'ArrowUp':
            case 'Up':
                if (isVertical) {
                    e.preventDefault();
                    tabGroup.focusPreviousTab(this, false);
                }
                break;
            case 'Home':
                e.preventDefault();
                tabGroup.focusFirstTab(false);
                break;
            case 'End':
                e.preventDefault();
                tabGroup.focusLastTab(false);
                break;
            case 'Enter':
            case ' ': {
                e.preventDefault();
                const tabId = this.getAttribute('id');
                tabGroup.activateTab(tabId);
                break;
            }
            default:
                break;
        }
    }

    setEventListeners() {
        const tabButton = this.shadowRoot.querySelector('button');
        if (tabButton) {
            tabButton.addEventListener('click', this.handleClick);
            tabButton.addEventListener('keydown', this.handleKeyDown);
        }
    }

    removeEventListeners() {
        const tabButton = this.shadowRoot.querySelector('button');
        if (tabButton) {
            tabButton.removeEventListener('click', this.handleClick);
            tabButton.removeEventListener('keydown', this.handleKeyDown);
        }
    }

    focus() {
        const tabButton = this.shadowRoot.querySelector('button');
        if (tabButton) {
            tabButton.focus();
        }
    }
}

customElements.define('neptune-tab', Tab);
export default Tab;